import { Link, LinkProps } from 'react-router-dom';
import { cn } from '../../../utils/cn';
import { initials } from '../../../lib/format';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import type { Tone } from '../../../types/common';
import type { PageMeta } from '../../../types/api';

const BADGE: Record<string, string> = {
  neutral: 'bg-navy-50 text-navy-600',
  blue: 'bg-brand-50 text-brand-700',
  sky: 'bg-sky-50 text-sky-700',
  yellow: 'bg-sun-50 text-sun-700',
  green: 'bg-emerald-50 text-emerald-700',
  red: 'bg-red-50 text-red-600',
  grey: 'bg-slate-100 text-slate-600',
};

export function Badge({ tone = 'neutral', dot, children, className }: { tone?: Tone | string; dot?: boolean; children?: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-[13px] font-medium', BADGE[tone], className)}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function Card({ className, children, as: Comp = 'div', ...rest }: HTMLAttributes<HTMLElement> & { as?: ElementType; [key: string]: any }) {
  return (
    <Comp className={cn('rounded-xl border border-line bg-white shadow-card', className)} {...rest}>
      {children}
    </Comp>
  );
}

export function CardHeader({ title, subtitle, action, className }: { title?: ReactNode; subtitle?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-start justify-between gap-3 px-5 pt-5', className)}>
      <div className="min-w-0">
        <h3 className="text-[15px] font-semibold">{title}</h3>
        {subtitle && <p className="mt-0.5 text-[13px] text-ink-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/** Small blue link for card headers and footers, e.g. "View all ->". */
export function CardLink({ to, children, arrow = true, className, ...rest }: LinkProps & { arrow?: boolean }) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded text-sm font-medium text-brand-600 outline-none hover:text-brand-700 hover:underline focus-visible:ring-2 focus-visible:ring-steel',
        className,
      )}
      {...rest}
    >
      {children}
      {arrow && <Icon name="arrowRight" size={15} />}
    </Link>
  );
}

export function Avatar({ name, size = 36, tone = 'light', className }: { name?: string; size?: number; tone?: 'light' | 'dark' | string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        tone === 'dark' ? 'bg-white/10 text-white' : 'bg-brand-50 text-brand-700',
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
      aria-hidden="true"
    >
      {initials(name) || '?'}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-lg bg-navy-100/70', className)} />;
}

/** Shown when a list has nothing yet: says what to do next. */
export function EmptyState({ icon = 'grid', title, message, action, className }: { icon?: string; title?: ReactNode; message?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
        <Icon name={icon} size={26} />
      </span>
      <h3 className="text-base font-semibold">{title}</h3>
      {message && <p className="mt-1 max-w-sm text-sm text-ink-500">{message}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, className }: { error?: { message?: string } | null; onRetry?: () => void; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-red-50 text-red-600">
        <Icon name="alert" size={26} />
      </span>
      <h3 className="text-base font-semibold">Couldn't load this</h3>
      <p className="mt-1 max-w-sm text-sm text-ink-500">{error?.message || 'Something went wrong.'}</p>
      {onRetry && (
        <Button variant="outline" leftIcon="refresh" className="mt-5" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Pagination({ meta, onPage, className }: { meta?: PageMeta | null; onPage: (page: number) => void; className?: string }) {
  if (!meta || meta.total === 0) return null;
  const from = (meta.page - 1) * meta.limit + 1;
  const to = Math.min(meta.page * meta.limit, meta.total);
  return (
    <div className={cn('flex items-center justify-between gap-3 text-sm text-ink-500', className)}>
      <span className="tabular">
        {from}–{to} of {meta.total}
      </span>
      <div className="flex items-center gap-1">
        <IconButton size="sm" variant="outline" icon="chevronLeft" label="Previous page" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)} />
        <span className="tabular px-2">
          Page {meta.page} of {Math.max(meta.pages, 1)}
        </span>
        <IconButton size="sm" variant="outline" icon="chevronRight" label="Next page" disabled={meta.page >= meta.pages} onClick={() => onPage(meta.page + 1)} />
      </div>
    </div>
  );
}

/**
 * Title row at the top of every screen.
 * eyebrow: small line above the title (e.g. today's date). badge: shown next to the title (e.g. status).
 * subtitle: small facts under the title. actions: buttons on the right.
 */
export function PageHeader({ title, subtitle, eyebrow, badge, actions }: { title?: ReactNode; subtitle?: ReactNode; eyebrow?: ReactNode; badge?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        {eyebrow && <p className="mb-1 text-sm text-ink-500">{eyebrow}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-[22px] font-semibold leading-tight tracking-tight">{title}</h1>
          {badge}
        </div>
        {subtitle && <div className="mt-1.5 text-sm text-ink-500">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Label/value pair for detail screens. */
export function InfoRow({ label, children }: { label?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[13px] text-ink-400">{label}</dt>
      <dd className="text-sm text-navy-900">{children || <span className="text-ink-300">Not added</span>}</dd>
    </div>
  );
}
